
import React, { useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface DeleteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  formName: string;
  onDelete: () => void;
}

const DeleteFormDialog: React.FC<DeleteFormDialogProps> = ({
  open,
  onOpenChange,
  formId,
  formName,
  onDelete
}) => {
  const [confirmation, setConfirmation] = useState('');
  const expectedConfirmation = 'DELETE';
  const deleteDisabled = confirmation !== expectedConfirmation;
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      // Instead of directly deleting the form, check if there are leads associated with it
      const { data: leadsCount, error: leadsError } = await supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .eq('form_id', formId);
        
      if (leadsError) throw leadsError;
      
      // Delete the form
      const { error } = await supabase
        .from('forms')
        .delete()
        .eq('id', formId);
        
      if (error) throw error;
      
      // Notify the user about preserved leads if any exist
      if (leadsCount && leadsCount.length > 0) {
        toast.success("Form deleted successfully. Your leads have been preserved.");
      } else {
        toast.success("Form deleted successfully");
      }
      
      onOpenChange(false);
      onDelete();
    } catch (error) {
      console.error("Error deleting form:", error);
      toast.error("Failed to delete form");
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">Delete Form: {formName}</AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            <span className="font-semibold">This action cannot be undone.</span> This will permanently delete the form.
            Any leads associated with this form will be preserved and can still be accessed from the Leads page.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="rounded-md bg-destructive/10 p-4 border border-destructive/20">
            <p className="text-sm text-destructive font-medium">
              To confirm deletion, type "DELETE" in the field below
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="delete-confirmation">Confirmation</Label>
            <Input
              id="delete-confirmation"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="border-destructive/50"
            />
          </div>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            disabled={deleteDisabled || isDeleting}
            className={`${deleteDisabled ? 'opacity-50 cursor-not-allowed' : ''} bg-destructive text-destructive-foreground hover:bg-destructive/90`}
          >
            {isDeleting ? "Deleting..." : "Delete Form"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteFormDialog;
